import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Medi as MediIcon, Send, Sparkles, Briefcase, GraduationCap, ArrowRight, Phone, Stethoscope } from '../components/Icons.jsx';
import ChatMarkdown from '../components/ChatMarkdown.jsx';
import { trackEvent } from '../lib/analytics.js';

const suggestions = [
  "I'm 8 weeks out from the UCAT — what should I drill first? 🧠",
  'Can you build me a week-by-week UCAT study plan?',
  'How do top applicants structure their personal statement?',
  'Which UK med schools suit my UCAT score best?'
];

// Soft per-browser cap (the real cost ceiling is the per-IP server limit in
// server/index.js). Kept generous so engaged students aren't cut off mid-prep.
// Bumping the key version also resets anyone currently stuck at the old cap.
const DAILY_LIMIT = 60;
const RATE_KEY = 'medi-rate-v2';

function readQuota() {
  const today = new Date().toISOString().slice(0, 10);
  try {
    const parsed = JSON.parse(localStorage.getItem(RATE_KEY) || '{}');
    if (parsed.date === today) return parsed.count || 0;
  } catch {}
  return 0;
}
function bumpQuota() {
  const today = new Date().toISOString().slice(0, 10);
  const count = readQuota() + 1;
  localStorage.setItem(RATE_KEY, JSON.stringify({ date: today, count }));
  return count;
}

export default function Medi() {
  const [text, setText] = useState('');
  const [messages, setMessages] = useState([]);
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState('');
  const [used, setUsed] = useState(readQuota);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, pending]);

  const remaining = Math.max(0, DAILY_LIMIT - used);

  const send = async (override) => {
    const content = (override ?? text).trim();
    if (!content || pending) return;
    if (remaining <= 0) {
      setErr(`You've used today's ${DAILY_LIMIT} messages. Comes back tomorrow — or book a free 15-min call with a real Medic Mind tutor.`);
      return;
    }
    setErr('');
    setText('');
    const nextMessages = [...messages, { role: 'user', content }];
    setMessages(nextMessages);
    setPending(true);
    try {
      const res = await fetch('/api/medi', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: nextMessages })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setMessages([...nextMessages, { role: 'assistant', content: data.message }]);
      setUsed(bumpQuota());
      trackEvent('ash_message_sent', { length: content.length });
    } catch (e) {
      setErr(e.message || 'Something went wrong talking to Medi.');
      setMessages(nextMessages);
    } finally {
      setPending(false);
    }
  };

  const reset = () => { setMessages([]); setErr(''); };

  return (
    <div className="medi-grid">
      <div>
        <div className="medi-head">
          <div className="avatar"><MediIcon /></div>
          <div>
            <h1>Ash</h1>
            <p>Your AI tutor on Medi. Ask anything — UCAT, GAMSAT, interviews, personal statement, work experience. Session-only · {remaining}/{DAILY_LIMIT} messages left today.</p>
          </div>
        </div>

        <div className="medi-card">
          {messages.length === 0 && (
            <div className="suggestion-chips">
              {suggestions.map((s) => (
                <button key={s} className="chip" onClick={() => send(s)}>{s}</button>
              ))}
            </div>
          )}

          {messages.length > 0 && (
            <div ref={scrollRef} style={{ maxHeight: 460, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 14, padding: '4px 2px' }}>
              {messages.map((m, i) => (
                <div key={i} style={{
                  alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: '88%',
                  padding: '12px 16px',
                  borderRadius: m.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                  background: m.role === 'user' ? 'var(--brand-deep)' : 'var(--card-soft)',
                  color: m.role === 'user' ? '#fff' : 'var(--text)',
                  fontSize: 14.5, lineHeight: 1.55,
                  ...(m.role === 'user' ? { whiteSpace: 'pre-wrap' } : {}),
                }}>
                  {m.role === 'user' ? m.content : <ChatMarkdown text={m.content} />}
                </div>
              ))}
              {pending && (
                <div style={{ alignSelf: 'flex-start', padding: '12px 16px', borderRadius: '16px 16px 16px 4px', background: 'var(--card-soft)', color: 'var(--text-muted)', fontSize: 14 }}>
                  Ash is thinking…
                </div>
              )}
            </div>
          )}

          {err && <div style={{ color: '#b91c1c', fontSize: 13, marginBottom: 10, padding: '8px 12px', background: '#fee2e2', borderRadius: 8 }}>{err}</div>}

          <div className="chat-input-wrap">
            <textarea
              placeholder={remaining > 0 ? 'Ask Ash anything UCAT…' : 'Daily limit reached — comes back tomorrow.'}
              value={text}
              disabled={remaining <= 0 || pending}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
              }}
            />
            <button
              className="send-btn"
              aria-label="Send"
              disabled={remaining <= 0 || pending || !text.trim()}
              onClick={() => send()}
            ><Send width="18" height="18" /></button>
          </div>

          {messages.length > 0 && (
            <button onClick={reset} style={{ marginTop: 10, background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>
              ↻ New conversation
            </button>
          )}
        </div>
      </div>

      <aside className="rail">
        <div>
          <div className="rail-head">Beyond Ash</div>
          <div className="rail-card">
            <h4>Real Medic Mind tutors, courses and 1-to-1 support</h4>
            <p>Ash is great for strategy. When you need a human, Medic Mind covers the full pathway.</p>
          </div>
        </div>

        <RailCard
          icon={<Phone width="16" height="16" />}
          kicker="Free 15-min call"
          title="Talk to a Medic Mind advisor"
          body="Shortlist your unis, sanity-check your UCAT score, plan your application."
          link="Book free call →"
          href="https://www.medicmind.co.uk/contact-us/"
        />

        <RailCard
          icon={<Stethoscope width="16" height="16" />}
          kicker="Medicine Career Camp"
          title="NHS placements + free virtual work experience"
          body="Real London, Birmingham and Manchester hospital + hospice placements — plus a free virtual option."
          link="See placements →"
          to="/career-camp"
        />

        <RailCard
          icon={<GraduationCap width="16" height="16" />}
          kicker="UCAT"
          title="Live 2-day UCAT crash course"
          body="99th-percentile tutors. Strategy, timing and section-by-section drills."
          link="View course →"
          href="https://www.medicmind.co.uk/contact-us/"
        />

        <p className="rail-note">Medi is an AI assistant. Always verify university thresholds and entry rules on each university's official page before applying.</p>
      </aside>
    </div>
  );
}

function RailCard({ icon, kicker, title, body, link, to, href }) {
  const content = (
    <>
      <div className="row-icon">
        <div className="badge">{icon}</div>
        <span>{kicker}</span>
      </div>
      <h4>{title}</h4>
      <p>{body}</p>
      <span className="link">{link}</span>
    </>
  );
  if (to) return <Link to={to} className="rail-card">{content}</Link>;
  if (href) return <a href={href} target="_blank" rel="noreferrer" className="rail-card" style={{ textDecoration: 'none' }}>{content}</a>;
  return <button className="rail-card" style={{ textAlign: 'left', width: '100%', cursor: 'pointer' }}>{content}</button>;
}
